/**
 * /admin/campaigns — list view with status tabs.
 *
 * Lists campaigns for a merchant by status. The status filter maps
 * to the GSI1-MerchantStatusIndex range-key prefix scan, so the
 * Lambda handles the index choice; we just pass merchant_id + status.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Campaign,
  CampaignStatus,
  listCampaigns,
} from "../../../api/campaigns";

const STATUS_TABS: { label: string; value: CampaignStatus | "all" }[] = [
  { label: "All", value: "all" },
  { label: "Draft", value: "draft" },
  { label: "Approved", value: "approved" },
  { label: "Sending", value: "sending" },
  { label: "Sent", value: "sent" },
  { label: "Held", value: "held" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Failed", value: "failed" },
];

const STATUS_BADGE: Record<CampaignStatus, string> = {
  draft: "bg-gray-200 text-gray-700",
  approved: "bg-blue-100 text-blue-700",
  sending: "bg-yellow-100 text-yellow-700 animate-pulse",
  sent: "bg-green-100 text-green-700",
  held: "bg-orange-100 text-orange-700",
  cancelled: "bg-gray-100 text-gray-500",
  failed: "bg-red-100 text-red-700",
};

const DEFAULT_MERCHANT_KEY = "add2wallet_default_merchant";

const CampaignList: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const [merchantId, setMerchantId] = useState(
    params.get("merchant_id") ||
      localStorage.getItem(DEFAULT_MERCHANT_KEY) ||
      "76"
  );
  const [activeTab, setActiveTab] = useState<CampaignStatus | "all">(
    (params.get("status") as CampaignStatus) || "all"
  );
  const [items, setItems] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await listCampaigns(
        merchantId,
        activeTab === "all" ? undefined : activeTab
      );
      setItems(res.items);
      // Cache the merchant ID so we land on it next visit.
      localStorage.setItem(DEFAULT_MERCHANT_KEY, merchantId);
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to load campaigns"
      );
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [merchantId, activeTab]);

  useEffect(() => {
    load();
  }, [load]);

  // Push current filters into the URL so refresh / bookmark works.
  useEffect(() => {
    const next: Record<string, string> = { merchant_id: merchantId };
    if (activeTab !== "all") next.status = activeTab;
    setParams(next, { replace: true });
  }, [merchantId, activeTab, setParams]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto py-10 px-6">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Push Campaigns</h1>
          <Link
            to="/admin/campaigns/new"
            className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700"
          >
            + New campaign
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 mb-4 flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700">
            Merchant ID
          </label>
          <input
            type="text"
            value={merchantId}
            onChange={(e) => setMerchantId(e.target.value)}
            onBlur={load}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm w-32"
          />
          <button
            onClick={load}
            disabled={loading}
            className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-md text-sm"
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="border-b border-gray-200 flex overflow-x-auto">
            {STATUS_TABS.map((t) => (
              <button
                key={t.value}
                onClick={() => setActiveTab(t.value)}
                className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${
                  activeTab === t.value
                    ? "border-b-2 border-purple-600 text-purple-700"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {error && (
            <div className="p-4 bg-red-50 text-red-700 text-sm">{error}</div>
          )}

          {!loading && !error && items.length === 0 && (
            <div className="p-8 text-center text-gray-500 text-sm">
              No campaigns found for merchant {merchantId}
              {activeTab !== "all" && ` with status "${activeTab}"`}.
            </div>
          )}

          {items.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-gray-600">Name</th>
                  <th className="px-4 py-2 text-left text-gray-600">Status</th>
                  <th className="px-4 py-2 text-left text-gray-600">
                    Scheduled
                  </th>
                  <th className="px-4 py-2 text-left text-gray-600">
                    Audience
                  </th>
                  <th className="px-4 py-2 text-left text-gray-600">
                    Progress
                  </th>
                  <th className="px-4 py-2 text-left text-gray-600">
                    Platform
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr
                    key={c.campaign_id}
                    className="border-t border-gray-100 hover:bg-gray-50"
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/campaigns/${c.campaign_id}`}
                        className="text-purple-700 hover:underline font-medium"
                      >
                        {c.name}
                      </Link>
                      <div className="text-xs text-gray-400 font-mono">
                        {c.campaign_id.substring(0, 12)}…
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded ${STATUS_BADGE[c.status]}`}
                      >
                        {c.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {fmtTime(c.scheduled_at)}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {c.audience_type === "merchant_all" ? "All" : "List"}
                      {c.target_count !== undefined &&
                        ` · ${c.target_count}`}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {renderProgress(c)}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{c.platform}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

function fmtTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

function renderProgress(c: Campaign): React.ReactNode {
  const target = c.target_count ?? 0;
  const sent = c.sent_count ?? 0;
  const failed = c.failed_count ?? 0;
  if (target === 0) return "—";
  const pct = Math.min(100, Math.round(((sent + failed) / target) * 100));
  return (
    <div>
      <div className="w-32 h-2 bg-gray-200 rounded overflow-hidden">
        <div
          className="h-full bg-purple-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="text-xs text-gray-500 mt-0.5">
        {sent}/{target}
        {failed > 0 && ` · ${failed} failed`}
      </div>
    </div>
  );
}

export default CampaignList;
